/**
 * Service Cache Database
 *
 * A generic, Dexie-backed cache for frontend service-call responses
 * (`.tasks/service-cache/plan.md`, decisions 1, 2, 4, 8).
 *
 * What this is: the shared store behind `useCachedCall`/`useCachedCalls`. It
 * holds **response-level** rows (one row per cache key holding the whole
 * resolved value — decision 8; item-level shredding was rejected), keyed by a
 * user-scoped key (`${userId}:${key}` — decision 4; a second account on the
 * same browser must never read the first account's cache). Entries are never
 * deleted on invalidation: an entry is flagged `stale` so it keeps serving its
 * data for stale-while-revalidate until a refetch overwrites it (decision 2).
 *
 * Consumers never touch Dexie directly — they use the module cache API
 * (`cachePut` / `cacheInvalidate` / `cacheInvalidatePrefix` / `cacheRemove`),
 * which takes `userId` as its first argument so the scope cannot be skipped.
 */

import Dexie, { type Table } from 'dexie';

// ============================================================================
// TYPES
// ============================================================================

/** One cached response row. */
export type CacheEntry = {
  key: string;
  value: unknown;
  /** Epoch ms of the last successful write. */
  fetchedAt: number;
  /** Set by invalidation; cleared by the next write. Stale rows still serve. */
  stale: boolean;
};

// ============================================================================
// DATABASE
// ============================================================================

export class ServiceCacheDatabase extends Dexie {
  entries!: Table<CacheEntry, string>;

  constructor(name = 'BloomstackServiceCache') {
    super(name);
    this.version(1).stores({
      entries: 'key',
    });
  }
}

/** Production singleton. Tests construct their own isolated database. */
export const serviceCacheDb = new ServiceCacheDatabase();

// ============================================================================
// ROW HELPERS
// ============================================================================

/** Read a row by its (already user-scoped) key. */
export async function readEntry(
  db: ServiceCacheDatabase,
  key: string,
): Promise<CacheEntry | undefined> {
  return db.entries.get(key);
}

/**
 * Upsert a row. A successful write always stamps `fetchedAt` with the current
 * time and clears `stale` — fresh data is by definition not stale.
 */
export async function putEntry(
  db: ServiceCacheDatabase,
  key: string,
  value: unknown,
): Promise<void> {
  await db.entries.put({ key, value, fetchedAt: Date.now(), stale: false });
}

/**
 * Pure freshness check: a row serves without a refetch only when it is not
 * flagged stale and its age is within `ttlMs`. `now` is injected so the check
 * is deterministic.
 */
export function isFresh(row: CacheEntry | undefined, ttlMs: number, now: number): boolean {
  if (!row) return false;
  return !row.stale && now - row.fetchedAt < ttlMs;
}

/**
 * Flag a row stale without touching `fetchedAt` or `value`. The row keeps
 * serving its data (SWR) until a refetch writes a fresh one.
 */
export async function setStale(db: ServiceCacheDatabase, key: string): Promise<void> {
  await db.entries.update(key, { stale: true });
}

/** Delete a row. */
export async function removeEntry(db: ServiceCacheDatabase, key: string): Promise<void> {
  await db.entries.delete(key);
}

/** Delete every row whose key starts with `prefix`. */
export async function removePrefix(db: ServiceCacheDatabase, prefix: string): Promise<void> {
  if (!prefix) return;
  await db.entries.where('key').startsWith(prefix).delete();
}

// ============================================================================
// DEBOUNCED INVALIDATE
// ============================================================================

/** Per-key debounce window for `cacheInvalidate`. */
export const DEBOUNCE_MS = 50;

type PendingKind = 'key' | 'prefix';

type PendingInvalidation = {
  db: ServiceCacheDatabase;
  kind: PendingKind;
  /** Full user-scoped key (kind === 'key') or user-scoped prefix (kind === 'prefix'). */
  target: string;
  timer: ReturnType<typeof setTimeout>;
};

/**
 * Pending invalidations, keyed by `${db.name}\0${kind}\0${target}` so different
 * keys (and different databases, in tests) never share a timer.
 */
const pending = new Map<string, PendingInvalidation>();

function pendingId(db: ServiceCacheDatabase, kind: PendingKind, target: string): string {
  return `${db.name}\u0000${kind}\u0000${target}`;
}

async function executeInvalidation(entry: PendingInvalidation): Promise<void> {
  const { db, kind, target } = entry;
  if (kind === 'key') {
    await setStale(db, target);
    return;
  }
  const rows = await db.entries.where('key').startsWith(target).primaryKeys();
  for (const key of rows) {
    await setStale(db, key);
  }
}

function schedule(db: ServiceCacheDatabase, kind: PendingKind, target: string): void {
  const id = pendingId(db, kind, target);
  if (pending.has(id)) return; // Already scheduled within the window — coalesce.

  const entry: PendingInvalidation = {
    db,
    kind,
    target,
    timer: setTimeout(() => {
      pending.delete(id);
      void executeInvalidation(entry);
    }, DEBOUNCE_MS),
  };
  pending.set(id, entry);
}

/**
 * Flag `${userId}:${key}` stale after `DEBOUNCE_MS`. Repeated calls for the same
 * `(userId, key)` inside the window coalesce into a single write; different
 * keys keep independent timers.
 */
export function invalidate(db: ServiceCacheDatabase, userId: string, key: string): void {
  schedule(db, 'key', `${userId}:${key}`);
}

/**
 * Test-only: force every pending invalidation to run immediately. Production
 * code never calls this — it exists so specs are deterministic.
 */
export async function flushInvalidates(): Promise<void> {
  const entries = [...pending.values()];
  pending.clear();
  for (const entry of entries) {
    clearTimeout(entry.timer);
  }
  for (const entry of entries) {
    await executeInvalidation(entry);
  }
}

// ============================================================================
// MODULE CACHE API
// ============================================================================

/**
 * Write a resolved value under the user-scoped key. `db` is injectable for
 * tests; production call sites omit it and hit the singleton.
 */
export async function cachePut(
  userId: string,
  key: string,
  value: unknown,
  db: ServiceCacheDatabase = serviceCacheDb,
): Promise<void> {
  await putEntry(db, `${userId}:${key}`, value);
}

/**
 * Flag a user-scoped key stale (no data → refetch on next read). Debounced
 * per key so an event burst coalesces into one write.
 */
export function cacheInvalidate(
  userId: string,
  key: string,
  db: ServiceCacheDatabase = serviceCacheDb,
): void {
  invalidate(db, userId, key);
}

/**
 * Flag every one of the user's keys carrying `prefix` stale. The read + writes
 * run as one debounced batch (not per key) — used by events whose payload
 * cannot identify a single workspace.
 */
export function cacheInvalidatePrefix(
  userId: string,
  prefix: string,
  db: ServiceCacheDatabase = serviceCacheDb,
): void {
  schedule(db, 'prefix', `${userId}:${prefix}`);
}

/**
 * Delete a user-scoped key immediately. Removal is not storm-prone, so it is
 * not debounced.
 */
export async function cacheRemove(
  userId: string,
  key: string,
  db: ServiceCacheDatabase = serviceCacheDb,
): Promise<void> {
  await removeEntry(db, `${userId}:${key}`);
}
